import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to origin so AuthCallback catches the session_id and routes correctly
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // After email login, check registration step
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userData = await login(email, password);
      
      console.log('[Login] User data received:', userData);
      
      // Force-change-password ALWAYS wins — superadmins, owners, anyone.
      // Until they rotate their temp credential we don't let them
      // anywhere else (the route guards in App.js back this up
      // server-side via the must-change-password redirect).
      if (userData.must_change_password) {
        navigate("/cambiar-password-obligatorio", { replace: true });
      } else if (userData.role === "superadmin") {
        navigate("/superadmin", { replace: true });
      } else if (userData.registration_step === "user_created" || (!userData.organization_id && userData.registration_step !== "complete")) {
        // User created account but hasn't created organization yet
        navigate("/register/organization", { replace: true });
      } else {
        // User has completed registration (has org) → go to dashboard
        // Note: "org_created" step no longer exists - users go straight to dashboard after org creation
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-cyan-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 text-center max-w-md">
          <img src="/notalia-logo.png" alt="Notalia" className="h-16 mx-auto mb-8 brightness-0 invert" />
          <h1 className="text-3xl font-bold text-white mb-4 font-display">
            Plataforma de Cumplimiento
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Gestiona tus obligaciones de LFPIORPI y LFPDPPP de manera simple, segura y eficiente.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-slate-950">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:text-left">
            <img src="/notalia-icon.png" alt="Notalia" className="h-10 mx-auto lg:mx-0 mb-6 brightness-0 invert" />
            <h2 className="text-2xl font-bold text-white" data-testid="login-heading">Iniciar Sesion</h2>
            <p className="text-slate-400 mt-1">Ingresa a tu cuenta de cumplimiento</p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Correo electronico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300">Contrasena</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-teal-400 hover:text-teal-300"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white font-medium h-11"
              data-testid="login-submit-button"
            >
              {loading ? "Ingresando..." : "Iniciar Sesion"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-slate-950 px-4 text-slate-500">o continua con</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white h-11"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continuar con Google
          </Button>

          <p className="text-center text-sm text-slate-500">
            No tienes cuenta?{" "}
            <Link to="/register" className="text-teal-400 hover:text-teal-300 font-medium" data-testid="register-link">
              Crear cuenta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
